import React, { useEffect, useRef } from "react";
import { Grid, InputLabel, FormControl, Select, MenuItem, Box, Link, } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '../../TextField';
import { chapter as chapterConst, SECTION_TYPE } from '../../../pages/course/lessonPlan/constants';
import Button from '../../Button';
import { useCourse } from '../../../pages/course/hooks';
import { isValidKeywordsMarkupContent, isValidMarkupContent } from "../../markupEditor/lib";
import { toast } from "react-toastify";
import ContentRenderers from "./contentRenderers";
import { videoFileFilter } from "../../../utils";
import { uploadImage } from "../../../store/course/courseBaseApi";


export const Chapter = ({ data, section }) => {

    const { lessonPlan, setChapters, updateChapter, deleteAssessment } = useCourse();
    const [expanded, setExpanded] = React.useState(false);
    const [chapterData, setChapterData] = React.useState(data);

    React.useEffect(() => {
        setChapterData(data);
    }, [data]);

    const [abortController, setAbortController] = React.useState(null);
    const [assessments, setAssessments] = React.useState([]);
    const [chapterTypes, setChapterTypes] = React.useState([]);
    const [loading, setLoading] = React.useState(false);


    const isValidMarkup = React.useMemo(() => {
        if (!chapterData.keyWords || !chapterData.content) {
            return true;
        }
        return (isValidKeywordsMarkupContent(chapterData.keyWords) && isValidMarkupContent(chapterData.content));
    }, [chapterData]);

    const onFileChange = async (file, field) => {
        const fileSizeInMB = (file.size / 1024 / 1024)
        if (!videoFileFilter(file.name)) {
            return toast.error('Please upload a valid file');
        } else if (fileSizeInMB > 20) {
            return toast.warn('Please upload file with in the 20 MB video');
        }
        'THU'
        const formData = new FormData();
        const id = chapterData?.chapterId;

        formData.append('file', file);
        const controller = new AbortController();
        setAbortController(controller);
        try {
            setLoading(true);
            const resp = await uploadImage({ id, formData, field });
            if (resp.data) {
                const chapterDetails = {
                    ...chapterData,
                    contentURL: resp.data
                }
                setChapterData(chapterDetails);
                onSave(chapterDetails)
            }
            if (resp.message) {
                toast.success(resp.message);
            }
        } catch (e) {
            return toast.error(e.error);
        } finally {
            setLoading(false);
            setAbortController(null);
        }
    }

    const handleCancelUpload = () => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setLoading(false);
        }
    }

    React.useEffect(() => {
        if (section?.sectionType) {
            const sectionTypeFilter = SECTION_TYPE.find(type => type.sectionType === section.sectionType)
            setChapterTypes(sectionTypeFilter?.chapterTypes || [])
        }
    }, [section])

    React.useEffect(() => {
        if (chapterData.assessments) {
            setAssessments(chapterData.assessments);
        }
    }, [lessonPlan]);

    const removeAssessment = (assessment) => {
        if (!confirm('Are you sure to delete this assessment?')) { return; }
        deleteAssessment({ id: assessment?.id })
    }

    const handleExpansion = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const chapterDetails = {
            ...chapterData,
            [name]: value
        }
        setChapterData(chapterDetails);
        onSave(chapterDetails)
    }

    const onSave = (chapterDetails) => {
        updateChapter({ section: section, chapter: chapterDetails });
        // setExpanded(false);
    }

    const removeChapter = (section, name) => {
        let sects = (lessonPlan?.sections || []).map(e => e);
        let ind = sects.findIndex(e => e.name == section);
        if (ind === -1) return;

        let _chapters = sects[ind]['chapters'].filter(e => e.name != name);
        setChapters({ name: section, chapters: _chapters });
        onSave()
    }

    const onContentSave = (sentances, keywords) => {
        const chapterDetails = {
            ...chapterData,
            content: sentances,
            keyWords: keywords
        }
        setChapterData(chapterDetails);
        onSave(chapterDetails);
    }

    const onContentUpload = (sentances) => {
        const chapterDetails = {
            ...chapterData,
            content: sentances
        }
        setChapterData(chapterDetails);
        onSave(chapterDetails);
    }

    return (
        <Accordion onChange={handleExpansion(data.name)}
            sx={{
                boxShadow: 'none',
                border: '1px solid #FFF2E6',
                borderRadius: '4px',
            }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                    background: '#FFF2E6',
                    boxShadow: 'none',
                    borderRadius: '4px',
                    minHeight: '48px !important',
                    '& .Mui-expanded': {
                        margin: '12px 0 !important',
                    }
                }}
            >
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ color: 'var(--mui-palette-primary-blue)', width: '80%', fontWeight: 'bold' }}>{data.name}</Box>
                    <Button sx={{ color: 'var(--mui-palette-primary-blue)', p: 0 }} onClick={() => removeChapter(section.name, data.name)} variant='text' placeholder={
                        <Link title="Delete Session" sx={{ color: 'var(--mui-palette-primary-blue)' }}><DeleteIcon /> </Link>
                    } />
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid sx={{ mt: 1 }} container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel style={styles.inputLabel}>Session Type <span style={{ color: 'red' }}>*</span></InputLabel>
                        <FormControl fullWidth>
                            <Select
                                name="chapterType"
                                value={chapterData.chapterType}
                                onChange={handleChange}
                                sx={{
                                    height: '47px',
                                    '& .MuiInputBase-root': {
                                        height: '47px',
                                    }
                                }}
                            >
                                {chapterTypes.map((type, i) => <MenuItem key={i} value={type.type}>{type.type}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel style={styles.inputLabel}>Type</InputLabel>
                        <FormControl fullWidth>
                            <Select
                                name="type"
                                value={chapterData.type}
                                onChange={handleChange}
                                sx={{
                                    height: '47px',
                                    '& .MuiInputBase-root': {
                                        height: '47px',
                                    }
                                }}
                            >
                                {Object.keys(chapterConst.TYPES).map((key, i) => <MenuItem key={i} value={key}>{chapterConst.TYPES[key]}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid sx={{ mt: 1 }} container spacing={2} alignItems="center">

                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel style={styles.inputLabel}>Name <span style={{ color: 'red' }}>*</span></InputLabel>
                        <TextField
                            name="name"
                            fullWidth
                            value={chapterData.name}
                            onChange={handleChange}
                            size='large'
                            placeholder="Enter section name"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel style={styles.inputLabel}>Content Type <span style={{ color: 'red' }}>*</span></InputLabel>
                        <FormControl fullWidth>
                            <Select
                                name="contentType"
                                value={chapterData.contentType}
                                onChange={handleChange}
                                sx={{
                                    height: '47px',
                                    '& .MuiInputBase-root': {
                                        height: '47px',
                                    }
                                }}
                            >
                                {Object.keys(chapterConst.CONTENT_TYPES).map((key, i) => <MenuItem key={i} value={key}>{chapterConst.CONTENT_TYPES[key]}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel style={styles.inputLabel}>Time (Min)</InputLabel>
                        <TextField
                            name="timeRequired"
                            fullWidth
                            type="number"
                            value={chapterData?.timeRequired}
                            onChange={handleChange}
                            placeholder="Session Time"
                        />
                    </Grid>

                    {/* <Grid item xs={12}>
                        <InputLabel style={styles.inputLabel}>Description</InputLabel>
                        <TextField
                            name="description"
                            fullWidth
                            multiline
                            rows={3}
                            value={chapterData.description}
                            onChange={handleChange}
                            placeholder="Enter section description"
                            required
                        />
                    </Grid> */}
                    <ContentRenderers
                        chapterType={chapterData?.chapterType}
                        chapterData={chapterData}
                        isValidMarkup={isValidMarkup}
                        onContentSave={onContentSave}
                        handleChange={handleChange}
                        sectionData={section}
                        onFileChange={onFileChange}
                        setChapterData={setChapterData}
                        onSave={onSave}
                        loading={loading}
                        onUpload={onContentUpload}
                        handleCancelUpload={handleCancelUpload}
                    />
                </Grid>
            </AccordionDetails>
            <AccordionActions sx={{ p: 2 }}>
                {/* {expanded && (
                    <>
                        <Button onClick={() => setExpanded(false)} variant='outlined' placeholder={'Cancel'} />
                        <Button onClick={onSave} variant='contained' placeholder={'Save'} />
                    </>
                )} */}
            </AccordionActions>
        </Accordion>
    )
};

const styles = {
    label: {
        color: 'var(--mui-palette-common-black)',
        fontWeight: 'bold'
    },
    inputLabel: {
        color: 'var(--mui-palette-primary-darkGrey)',
        marginBottom: '5px',
        fontWeight: '600',
        fontSize: '14px'

    },
    assessmentsLink: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '5px'
    },
    actionBtn: {
        display: 'flex',
        alignItems: 'center'
    },
}